/**
 * 文档抽屉查看器组件
 * 在右侧面板显示完整文档内容，支持分块导航和高亮
 */

import React, { useEffect, useRef, useState } from 'react';
import {
    X,
    FileText,
    Loader2,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    ChevronDown,
    ZoomIn,
    ZoomOut,
    Maximize2,
    Minimize2,
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

interface DocumentChunk {
    content: string;
    chunkIndex: number;
    charStart: number;
    charEnd: number;
    pageNumber?: number;
}

export interface DocumentDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    documentId: string;
    documentName: string;
    highlightChunkIndex: number;
    onNavigateChunk?: (newChunkIndex: number) => void;
}

export const DocumentDrawer: React.FC<DocumentDrawerProps> = ({
    isOpen,
    onClose,
    documentId,
    documentName,
    highlightChunkIndex,
    onNavigateChunk,
}) => {
    const [chunks, setChunks] = useState<DocumentChunk[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentChunkIndex, setCurrentChunkIndex] = useState(highlightChunkIndex);
    const [isExpanded, setIsExpanded] = useState(false);
    const [fontSize, setFontSize] = useState(14);
    const highlightRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    // 获取文档分块
    useEffect(() => {
        if (!isOpen || !documentId) return;

        const fetchChunks = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = localStorage.getItem('auth_token');
                const headers: Record<string, string> = {};
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }
                const res = await fetch(
                    `${API_BASE_URL}/api/knowledge/documents/${documentId}/chunks`,
                    { headers }
                );
                const response = await res.json();
                if (response.success && response.chunks) {
                    setChunks(response.chunks);
                } else {
                    setError(response.message || '无法加载文档内容');
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : '加载文档失败');
            } finally {
                setLoading(false);
            }
        };

        fetchChunks();
    }, [isOpen, documentId]);

    // 更新当前高亮索引
    useEffect(() => {
        setCurrentChunkIndex(highlightChunkIndex);
    }, [highlightChunkIndex]);

    // 自动滚动到高亮段落
    useEffect(() => {
        if (!loading && highlightRef.current) {
            setTimeout(() => {
                highlightRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }, 100);
        }
    }, [loading, currentChunkIndex]);

    // 导航到上一个/下一个分块
    const navigateChunk = (direction: 'prev' | 'next') => {
        const newIndex =
            direction === 'prev'
                ? Math.max(0, currentChunkIndex - 1)
                : Math.min(chunks.length - 1, currentChunkIndex + 1);

        setCurrentChunkIndex(newIndex);
        onNavigateChunk?.(newIndex);

        // 滚动到新位置
        const element = document.getElementById(`drawer-chunk-${newIndex}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // 字体大小调整
    const adjustFontSize = (delta: number) => {
        setFontSize((prev) => Math.max(12, Math.min(20, prev + delta)));
    };

    if (!isOpen) return null;

    return (
        <div
            className={`fixed top-0 right-0 h-full z-40 flex flex-col
                  bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700
                  shadow-2xl shadow-black/20 transition-all duration-300 ease-out
                  ${isExpanded ? 'w-full md:w-2/3' : 'w-full md:w-[480px]'}`}
        >
            {/* 头部 */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-cyan-50 dark:from-gray-800 dark:to-gray-800">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 bg-gradient-to-br from-purple-500 to-cyan-500 rounded-lg shadow-md">
                        <FileText className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h2 className="font-semibold text-gray-900 dark:text-white truncate">
                            {documentName}
                        </h2>
                        <p className="text-sm text-gray-500">
                            {loading
                                ? '加载中...'
                                : error
                                    ? '加载失败'
                                    : `${chunks.length} 个段落 · 当前段落 ${currentChunkIndex + 1}`}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-1">
                    {/* 字体大小 */}
                    <button
                        onClick={() => adjustFontSize(-1)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="缩小字体"
                    >
                        <ZoomOut className="w-4 h-4" />
                    </button>
                    <span className="text-xs text-gray-500 min-w-[32px] text-center">{fontSize}px</span>
                    <button
                        onClick={() => adjustFontSize(1)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="放大字体"
                    >
                        <ZoomIn className="w-4 h-4" />
                    </button>

                    {/* 展开/收缩 */}
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ml-2"
                        title={isExpanded ? '收窄' : '展开'}
                    >
                        {isExpanded ? (
                            <Minimize2 className="w-4 h-4" />
                        ) : (
                            <Maximize2 className="w-4 h-4" />
                        )}
                    </button>

                    {/* 关闭按钮 */}
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-500 hover:text-red-600 rounded-lg transition-colors ml-2"
                        title="关闭"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* 段落导航 */}
            {!loading && !error && chunks.length > 0 && (
                <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700/50">
                    <button
                        onClick={() => navigateChunk('prev')}
                        disabled={currentChunkIndex === 0}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium
                       bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600
                       rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronUp className="w-4 h-4" />
                        上一段
                    </button>

                    <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-500">
                            {currentChunkIndex + 1} / {chunks.length}
                        </span>
                        {chunks[currentChunkIndex]?.pageNumber && (
                            <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded-full">
                                第 {chunks[currentChunkIndex].pageNumber} 页
                            </span>
                        )}
                    </div>

                    <button
                        onClick={() => navigateChunk('next')}
                        disabled={currentChunkIndex === chunks.length - 1}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium
                       bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600
                       rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        下一段
                        <ChevronDown className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* 内容区域 */}
            <div ref={contentRef} className="flex-1 overflow-y-auto p-4 space-y-4">
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-64 gap-3">
                        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                        <p className="text-sm text-gray-500">正在加载文档内容...</p>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center h-64 gap-3 text-red-500">
                        <p>{error}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm"
                        >
                            重试
                        </button>
                    </div>
                ) : (
                    chunks.map((chunk) => {
                        const isHighlighted = chunk.chunkIndex === currentChunkIndex;
                        return (
                            <div
                                key={chunk.chunkIndex}
                                id={`drawer-chunk-${chunk.chunkIndex}`}
                                ref={isHighlighted ? highlightRef : null}
                                onClick={() => {
                                    setCurrentChunkIndex(chunk.chunkIndex);
                                    onNavigateChunk?.(chunk.chunkIndex);
                                }}
                                className={`
                  p-4 rounded-xl border-2 cursor-pointer
                  transition-all duration-300 ease-out
                  ${isHighlighted
                                        ? 'bg-gradient-to-br from-purple-50 to-cyan-50 dark:from-purple-900/30 dark:to-cyan-900/30 border-purple-400 dark:border-purple-500 ring-4 ring-purple-200/50 dark:ring-purple-800/30 shadow-lg scale-[1.02]'
                                        : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 hover:border-purple-200 dark:hover:border-purple-700 hover:shadow-md'
                                    }
                `}
                            >
                                {/* 段落元信息 */}
                                <div className="flex items-center gap-2 mb-3 text-xs">
                                    <span
                                        className={`
                      px-2 py-1 rounded-full font-medium
                      ${isHighlighted
                                                ? 'bg-purple-500 text-white'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                                            }
                    `}
                                    >
                                        段落 {chunk.chunkIndex + 1}
                                    </span>
                                    {chunk.pageNumber && (
                                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-500 rounded-full">
                                            第 {chunk.pageNumber} 页
                                        </span>
                                    )}
                                    {isHighlighted && (
                                        <span className="px-2 py-1 bg-gradient-to-r from-purple-500 to-cyan-500 text-white rounded-full animate-pulse">
                                            📍 引用来源
                                        </span>
                                    )}
                                </div>

                                {/* 段落内容 */}
                                <p
                                    className={`
                    leading-relaxed whitespace-pre-wrap
                    ${isHighlighted
                                            ? 'text-gray-900 dark:text-white'
                                            : 'text-gray-600 dark:text-gray-400'
                                        }
                  `}
                                    style={{ fontSize: `${fontSize}px` }}
                                >
                                    {chunk.content}
                                </p>
                            </div>
                        );
                    })
                )}
            </div>

            {/* 底部信息 */}
            {!loading && !error && (
                <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-xs text-gray-500 flex items-center justify-between">
                    <span>文档 ID: {documentId}</span>
                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                        已加载 {chunks.length} 个段落
                    </span>
                </div>
            )}
        </div>
    );
};

export default DocumentDrawer;
